/**
 * 管理后台：工具管理
 */
import { useState } from 'react';

export default function useAdminToolsData({ apiRequest }) {
  const [tools, setTools] = useState([]);
  const [toolName, setToolName] = useState('');
  const [toolUrl, setToolUrl] = useState('');
  const [toolDescription, setToolDescription] = useState('');
  const [toolAudience, setToolAudience] = useState('public');
  const [toolGroupIds, setToolGroupIds] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editBuiltin, setEditBuiltin] = useState(false);
  const [editMobileRelease, setEditMobileRelease] = useState(false);
  const [editSlug, setEditSlug] = useState('');
  const [toolVersionName, setToolVersionName] = useState('');
  const [toolVersionCode, setToolVersionCode] = useState('');
  const [toolMsg, setToolMsg] = useState('');
  const [toolUploading, setToolUploading] = useState(false);

  const resetToolForm = () => {
    setEditId(null);
    setEditBuiltin(false);
    setEditMobileRelease(false);
    setEditSlug('');
    setToolName('');
    setToolUrl('');
    setToolDescription('');
    setToolVersionName('');
    setToolVersionCode('');
    setToolAudience('public');
    setToolGroupIds([]);
  };

  const fetchTools = async () => {
    const data = await apiRequest('/api/tools/admin-list');
    if (data.success) {
      setTools(data.tools || []);
      return;
    }
    const fallback = await apiRequest('/api/tools/list');
    if (fallback.success) setTools(fallback.tools || []);
    else setToolMsg([data.error, data.detail].filter(Boolean).join('：') || fallback.error || '获取工具列表失败');
  };
  const handleUploadToolFile = async (file) => {
    if (!file) return;
    setToolUploading(true);
    setToolMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const slugQs = editSlug ? `?slug=${encodeURIComponent(editSlug)}` : '';
      const data = await apiRequest(`/api/tools/upload${slugQs}`, { method: 'POST', body: formData });
      if (data.success && data.url) {
        setToolUrl(data.url);
        const original = String(data.originalName || file.name || '');
        if (!toolName.trim()) {
          const base = original.replace(/\.[^.]+$/, '');
          if (base) setToolName(base);
        }
        const ver = original.match(/v?(\d+\.\d+(?:\.\d+)?)[-_](\d{1,6})\.(apk|hap)$/i);
        if (data.versionName || data.versionCode) {
          if (data.versionName) setToolVersionName(String(data.versionName));
          if (data.versionCode) setToolVersionCode(String(data.versionCode));
        } else if (ver) {
          setToolVersionName(ver[1]);
          setToolVersionCode(String(Number(ver[2]) || ''));
        }
        const stored = String(data.filename || '');
        const parsedHint = (data.versionName || data.versionCode)
          ? `，已从安装包读取版本 ${data.versionName || '—'}（构建 ${data.versionCode || '—'}）并写入更新通道`
          : '，请确认版本号后点击「添加/更新」保存';
        setToolMsg(
          stored && stored !== original
            ? `上传成功：${original}（服务器保存为 ${stored}）${parsedHint}`
            : `上传成功：${original}${parsedHint}`
        );
        if (data.versionName || data.versionCode) fetchTools();
      } else {
        setToolMsg([data.error, data.detail].filter(Boolean).join('：') || data.error || '上传失败');
      }
    } catch (error) {
      setToolMsg(error.message || '上传失败');
    } finally {
      setToolUploading(false);
    }
  };
  const handleSaveTool = async () => {
    if (!toolName || (!toolUrl && !editBuiltin)) {
      return setToolMsg('请填写工具名称，并上传文件或填写下载链接');
    }
    if (editMobileRelease) {
      const code = Number(toolVersionCode);
      if (!String(toolVersionName || '').trim() || !Number.isFinite(code) || code < 1) {
        return setToolMsg('单兵更新包必须填写版本名和构建号（构建号须大于手机已装版本）');
      }
    }
    setToolMsg('');
    const url = editId ? '/api/tools/update' : '/api/tools/add';
    const body = {
      name: toolName,
      url: toolUrl,
      description: toolDescription,
      audience: toolAudience,
      group_ids: toolGroupIds,
      version_name: toolVersionName,
      version_code: toolVersionCode ? Number(toolVersionCode) : null
    };
    if (editId) body.id = editId;
    const data = await apiRequest(url, { method: 'POST', body: JSON.stringify(body) });
    if (data.success) {
      setToolMsg('保存成功');
      resetToolForm();
      fetchTools();
    } else {
      setToolMsg([data.error, data.detail].filter(Boolean).join('：') || '保存失败');
    }
  };
  const handleUpdateToolAccess = async (tool, audience, groupIds) => {
    const nextAudience = audience || 'public';
    const nextGroups = nextAudience === 'groups' ? (groupIds || []) : [];
    setTools((prev) => (prev || []).map((row) => (
      row.id === tool.id
        ? { ...row, audience: nextAudience, group_ids: nextGroups }
        : row
    )));
    const data = await apiRequest('/api/tools/access', {
      method: 'POST',
      body: JSON.stringify({ id: tool.id, audience: nextAudience, group_ids: nextGroups })
    });
    if (!data.success) {
      setToolMsg(data.error || '权限保存失败');
      fetchTools();
      return;
    }
    fetchTools();
  };
  const handleDeleteTool = async (id) => {
    if (!window.confirm('确定要删除该工具吗？若为上传文件，服务器上的文件也会删除。')) return;
    const data = await apiRequest('/api/tools/delete', { method: 'POST', body: JSON.stringify({ id }) });
    if (data.success) fetchTools();
    else setToolMsg(data.error || '删除失败');
  };
  const handleEditTool = (tool) => {
    const slug = String(tool.slug || '');
    setEditId(tool.id);
    setEditBuiltin(!!tool.is_builtin || slug === 'google_earth');
    setEditMobileRelease(slug === 'zhxt_mobile_android' || slug === 'zhxt_mobile_harmony' || slug === 'zhxt_locus_android');
    setEditSlug(slug);
    setToolName(tool.name);
    setToolUrl(tool.url);
    setToolDescription(tool.description || '');
    setToolVersionName(tool.version_name || '');
    setToolVersionCode(tool.version_code ? String(tool.version_code) : '');
    setToolAudience(tool.audience || (tool.group_ids?.length ? 'groups' : 'public'));
    setToolGroupIds(Array.isArray(tool.group_ids) ? tool.group_ids.map(Number) : []);
  };

  return {
    tools, toolName, setToolName, toolUrl, setToolUrl, toolDescription, setToolDescription,
    toolVersionName, setToolVersionName, toolVersionCode, setToolVersionCode,
    toolAudience, setToolAudience, toolGroupIds, setToolGroupIds,
    editId, setEditId, editBuiltin, editMobileRelease, editSlug, toolMsg, toolUploading,
    fetchTools, handleSaveTool, handleUpdateToolAccess, handleUploadToolFile,
    handleEditTool, handleDeleteTool, resetToolForm
  };
}
